import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';
import { 
  FaEye, FaSearch, FaFilter, FaCalendarAlt, FaUsers, 
  FaSignInAlt, FaSignOutAlt, FaMobileAlt, FaMapMarkerAlt,
  FaClock, FaUserCircle, FaDownload, FaSync
} from 'react-icons/fa';

const AppTilesDealerAccessLog = () => {
  const { user } = useContext(AuthContext);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Default date range: today to tomorrow
  const today = new Date().toISOString().split('T')[0];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
  const [dateRange, setDateRange] = useState({
    startDate: today,
    endDate: tomorrow
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // Dark mode
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = localStorage.getItem('darkMode') === 'true';
      setDarkMode(isDark);
    };
    const interval = setInterval(checkDarkMode, 500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchAccessLogs();
  }, [currentPage]);

  const fetchAccessLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {
        page: currentPage,
        limit: 20,
        search: searchTerm,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
      };

      const response = await axios.get(`${API_BASE}/dealer-access-logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      if (response.data.success) {
        setLogs(response.data.data.logs);
        setTotalPages(response.data.data.totalPages);
        setTotalLogs(response.data.data.total);
      }
    } catch (error) {
      console.error('Error fetching access logs:', error);
      Swal.fire('Error', 'Failed to fetch access logs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchAccessLogs();
  };

  const handleDateFilter = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchAccessLogs();
  };

  const exportLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {
        search: searchTerm,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        export: true
      };

      const response = await axios.get(`${API_BASE}/dealer-access-logs/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: 'blob'
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dealer-access-logs-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      Swal.fire('Success', 'Access logs exported successfully', 'success');
    } catch (error) {
      console.error('Error exporting logs:', error);
      Swal.fire('Error', 'Failed to export access logs', 'error');
    }
  };

  const getActionIcon = (action) => {
    const icons = {
      login: <FaSignInAlt className="text-green-500" />,
      logout: <FaSignOutAlt className="text-red-500" />,
      view: <FaEye className="text-blue-500" />,
      scan: <FaMobileAlt className="text-purple-500" />,
      search: <FaSearch className="text-orange-500" />,
      profile_update: <FaUserCircle className="text-indigo-500" />
    };
    return icons[action] || <FaEye className="text-gray-500" />;
  };

  const getActionBadge = (action) => {
    const styles = {
      login: darkMode ? 'bg-green-900/40 text-green-400 border-green-700' : 'bg-green-100 text-green-700 border-green-200',
      logout: darkMode ? 'bg-red-900/40 text-red-400 border-red-700' : 'bg-red-100 text-red-700 border-red-200',
      view: darkMode ? 'bg-blue-900/40 text-blue-400 border-blue-700' : 'bg-blue-100 text-blue-700 border-blue-200',
      scan: darkMode ? 'bg-purple-900/40 text-purple-400 border-purple-700' : 'bg-purple-100 text-purple-700 border-purple-200',
      search: darkMode ? 'bg-orange-900/40 text-orange-400 border-orange-700' : 'bg-orange-100 text-orange-700 border-orange-200',
      profile_update: darkMode ? 'bg-indigo-900/40 text-indigo-400 border-indigo-700' : 'bg-indigo-100 text-indigo-700 border-indigo-200',
    };
    return styles[action] || (darkMode ? 'bg-gray-700 text-gray-300 border-gray-600' : 'bg-gray-100 text-gray-700 border-gray-200');
  };

  const getActionLabel = (action) => {
    const labels = {
      login: 'Login',
      logout: 'Logout',
      view: 'View',
      scan: 'QR Scan',
      search: 'Search',
      profile_update: 'Profile Update'
    };
    return labels[action] || action;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className={`p-6 min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className={`text-3xl font-bold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <FaEye className={`mr-3 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              App Dealer Access Logs
            </h1>
            <p className={`mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Monitor dealer app activity and access patterns</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={fetchAccessLogs}
              className={`px-4 py-2 rounded-lg flex items-center font-medium transition-colors ${
                darkMode ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              <FaSync className="mr-2" />
              Refresh
            </button>
            <button
              onClick={exportLogs}
              className={`px-4 py-2 rounded-lg flex items-center font-medium transition-colors ${
                darkMode ? 'bg-green-600 hover:bg-green-500 text-white' : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              <FaDownload className="mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className={`p-4 rounded-xl border transition-colors ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <div className="flex items-center">
            <div className={`p-3 rounded-xl ${darkMode ? 'bg-blue-900/40' : 'bg-blue-100'}`}>
              <FaEye className={`text-xl ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
            <div className="ml-4">
              <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Logs</p>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{totalLogs}</p>
            </div>
          </div>
        </div>

        <div className={`p-4 rounded-xl border transition-colors ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <div className="flex items-center">
            <div className={`p-3 rounded-xl ${darkMode ? 'bg-green-900/40' : 'bg-green-100'}`}>
              <FaSignInAlt className={`text-xl ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
            </div>
            <div className="ml-4">
              <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Today's Logins</p>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {logs.filter(log => 
                  log.action === 'login' && 
                  new Date(log.createdAt).toDateString() === new Date().toDateString()
                ).length}
              </p>
            </div>
          </div>
        </div>

        <div className={`p-4 rounded-xl border transition-colors ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <div className="flex items-center">
            <div className={`p-3 rounded-xl ${darkMode ? 'bg-purple-900/40' : 'bg-purple-100'}`}>
              <FaMobileAlt className={`text-xl ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
            </div>
            <div className="ml-4">
              <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>QR Scans</p>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {logs.filter(log => log.action === 'scan').length}
              </p>
            </div>
          </div>
        </div>

        <div className={`p-4 rounded-xl border transition-colors ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <div className="flex items-center">
            <div className={`p-3 rounded-xl ${darkMode ? 'bg-orange-900/40' : 'bg-orange-100'}`}>
              <FaUsers className={`text-xl ${darkMode ? 'text-orange-400' : 'text-orange-600'}`} />
            </div>
            <div className="ml-4">
              <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Active Dealers</p>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {new Set(logs.map(log => log.dealerId)).size}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className={`p-4 rounded-xl border mb-6 transition-colors ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className="grid grid-cols-1 md:grid-cols-10 gap-4">
          {/* Search */}
          <div className="md:col-span-4">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <FaSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  type="text"
                  placeholder="Search by dealer name..."
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </form>
          </div>

          {/* Date Range */}
          <div className="md:col-span-2">
            <input
              type="date"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white [color-scheme:dark]' : 'bg-white border-gray-300 text-gray-900'
              }`}
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
            />
          </div>
          <div className="md:col-span-2">
            <input
              type="date"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white [color-scheme:dark]' : 'bg-white border-gray-300 text-gray-900'
              }`}
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
            />
          </div>

          {/* Filter Button */}
          <div className="md:col-span-2">
            <button
              onClick={handleDateFilter}
              className={`w-full px-4 py-2 rounded-lg flex items-center justify-center font-medium transition-colors ${
                darkMode ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              <FaFilter className="mr-2" />
              Filter
            </button>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className={`rounded-xl border overflow-hidden transition-colors ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={darkMode ? 'bg-gray-700/60' : 'bg-gray-50'}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Timestamp
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Dealer
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Action
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Details
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Device Info
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  IP Address
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
                      <span className={`mt-3 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading access logs...</span>
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <FaEye className={`mx-auto mb-3 text-3xl ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                    <p className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No access logs found</p>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Try adjusting your filters</p>
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log._id} className={`transition-colors ${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`flex items-center text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                        <FaClock className={`mr-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                        {formatDate(log.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            <FaUserCircle className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                          </div>
                        </div>
                        <div className="ml-3">
                          <div className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                            {log.dealerDetails?.dealerName || 'Unknown Dealer'}
                          </div>
                          <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                            {log.dealerDetails?.companyName || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getActionBadge(log.action)}`}>
                        {getActionIcon(log.action)}
                        <span className="ml-1.5">{getActionLabel(log.action)}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                        {log.details ? (
                          <div className="max-w-xs truncate" title={log.details}>
                            {log.details}
                          </div>
                        ) : (
                          <span className={darkMode ? 'text-gray-600' : 'text-gray-400'}>-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                        {log.deviceInfo ? (
                          <div>
                            <div className="flex items-center">
                              <FaMobileAlt className={`mr-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                              <span className="text-xs">
                                {log.deviceInfo.platform || 'Unknown'}
                              </span>
                            </div>
                            {log.deviceInfo.version && (
                              <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                v{log.deviceInfo.version}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className={darkMode ? 'text-gray-600' : 'text-gray-400'}>-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                        <FaMapMarkerAlt className={`mr-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                        {log.ipAddress || 'Unknown'}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`px-4 py-3 flex items-center justify-between border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Page {currentPage} of {totalPages} ({totalLogs} total logs)
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1.5 border rounded-lg text-sm font-medium transition-colors disabled:opacity-40 ${
                  darkMode
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700 disabled:hover:bg-transparent'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50 disabled:hover:bg-white'
                }`}
              >
                Previous
              </button>
              
              <div className="hidden md:flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1.5 border rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNum
                          ? darkMode
                            ? 'bg-blue-600 border-blue-500 text-white'
                            : 'bg-blue-50 border-blue-500 text-blue-600'
                          : darkMode
                            ? 'border-gray-600 text-gray-400 hover:bg-gray-700'
                            : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1.5 border rounded-lg text-sm font-medium transition-colors disabled:opacity-40 ${
                  darkMode
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700 disabled:hover:bg-transparent'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50 disabled:hover:bg-white'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppTilesDealerAccessLog;